/**
 * Command Palette / Global Search.
 *
 * A modal search overlay triggered by Ctrl/Cmd+K. Searches across all
 * entity types and provides quick navigation to any result.
 * Glassmorphic design with keyboard navigation.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Package,
  FileText,
  Wrench,
  ShoppingCart,
  Users,
  Truck,
} from "lucide-react";
import { apiClient } from "@/api/client";

interface SearchResult {
  entity_type: string;
  entity_id: string;
  label: string;
  sublabel: string;
  path: string;
}

const entityIcons: Record<string, React.ElementType> = {
  asset: Package,
  purchase_order: FileText,
  work_order: Wrench,
  sales_order: ShoppingCart,
  customer: Users,
  vendor: Truck,
};

const entityLabels: Record<string, string> = {
  asset: "Asset",
  purchase_order: "Purchase Order",
  work_order: "Work Order",
  sales_order: "Sales Order",
  customer: "Customer",
  vendor: "Vendor",
};

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);

  // Focus input when opened.
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setResults([]);
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Debounced search.
  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const resp = await apiClient.get(
          `/api/v1/search?q=${encodeURIComponent(query)}`,
        );
        const data = resp.data as { results: SearchResult[] };
        setResults(data.results);
        setSelectedIndex(0);
      } catch {
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [query]);

  // Keyboard navigation.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" && results[selectedIndex]) {
        e.preventDefault();
        navigate(results[selectedIndex].path);
        onClose();
      } else if (e.key === "Escape") {
        onClose();
      }
    },
    [results, selectedIndex, navigate, onClose],
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Palette */}
      <div className="relative w-full max-w-lg animate-scale-in">
        <div className="glass-heavy rounded-[var(--radius-2xl)] shadow-lg overflow-hidden">
          {/* Search Input */}
          <div className="flex items-center gap-3 border-b border-[var(--color-border)] px-4">
            <Search className="h-5 w-5 text-[var(--text-tertiary)]" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search assets, orders, customers, vendors..."
              className="h-12 flex-1 bg-transparent text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none text-sm"
            />
            <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded-[var(--radius-sm)] border border-[var(--color-border)] px-1.5 py-0.5 text-[10px] text-[var(--text-tertiary)]">
              ESC
            </kbd>
          </div>

          {/* Results */}
          {results.length > 0 && (
            <div className="max-h-80 overflow-y-auto p-2 stagger-children">
              {results.map((result, i) => {
                const Icon = entityIcons[result.entity_type] || Package;
                return (
                  <button
                    key={`${result.entity_type}-${result.entity_id}`}
                    onClick={() => {
                      navigate(result.path);
                      onClose();
                    }}
                    className={`flex w-full items-center gap-3 rounded-[var(--radius-lg)] px-3 py-2.5 text-left transition-colors ${
                      i === selectedIndex
                        ? "bg-brand-600/10 text-brand-600 dark:bg-brand-400/10 dark:text-brand-300"
                        : "text-[var(--text-primary)] hover:bg-[var(--color-surface-hover)]"
                    }`}
                  >
                    <div
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-md)] ${
                        i === selectedIndex
                          ? "bg-brand-600/15"
                          : "bg-[var(--color-surface-tertiary)]"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {result.label}
                      </p>
                      <p className="truncate text-xs text-[var(--text-tertiary)]">
                        {entityLabels[result.entity_type] || result.entity_type}{" "}
                        · {result.sublabel}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Empty state */}
          {query.length >= 2 && results.length === 0 && !isSearching && (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-[var(--text-tertiary)]">
                No results for "{query}"
              </p>
            </div>
          )}

          {/* Loading */}
          {isSearching && (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-[var(--text-tertiary)]">
                Searching...
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-[var(--color-border)] px-4 py-2 flex items-center gap-4 text-[10px] text-[var(--text-tertiary)]">
            <span>
              <kbd className="font-mono">↑↓</kbd> navigate
            </span>
            <span>
              <kbd className="font-mono">↵</kbd> open
            </span>
            <span>
              <kbd className="font-mono">esc</kbd> close
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
